import { tamanhoVisivel } from '@core/lexico/normalizacao';

/**
 * O som do jogo (§24).
 *
 * Sintetizado na hora, sem arquivo nenhum: nada aqui passa de um décimo de
 * segundo, e baixar amostras custaria mais bytes do que o código que as
 * desenha — §16 conta cada um deles.
 *
 * A tecla **não é uma nota**. Uma senoide afinada soa a videogame: o ouvido
 * reconhece a altura, procura a melodia e não acha nenhuma. O que uma tecla
 * faz é um ruído — o estalo do mecanismo, e logo abaixo o baque surdo do
 * curso batendo no fim. São duas camadas, ruído filtrado e um corpo grave,
 * ambas com queda de poucos centésimos: é assim que soa teclado, e é a
 * ausência de altura definida que faz a diferença entre digitar e apitar.
 *
 * O volume é deliberadamente baixo. Este é um jogo de escrever, e o som existe
 * para confirmar que a tecla entrou — não para ser ouvido do outro cômodo.
 */

export type Som = 'letra' | 'apagar' | 'clique' | 'jogada';

/**
 * Uma camada de som.
 *
 * `ruido` é o estalo — banda estreita de ruído branco, e é dela que vem o
 * caráter mecânico. `tom` é o corpo, uma senoide grave curta demais para ter
 * melodia, ou o sino de fim de linha, que é o único lugar onde uma altura
 * definida é o que se quer.
 */
type Camada =
  | {
      readonly fonte: 'ruido';
      /** Centro da banda: onde mora o timbre do estalo. */
      readonly hz: number;
      readonly q: number;
      readonly duracao: number;
      readonly volume: number;
      readonly atraso?: number;
    }
  | {
      readonly fonte: 'tom';
      readonly hz: number;
      readonly duracao: number;
      readonly volume: number;
      readonly atraso?: number;
    };

const CAMADAS: Record<Som, readonly Camada[]> = {
  /*
   * O estalo em 2,8 kHz é a lâmina do mecanismo; o baque em 170 Hz é o curso
   * chegando ao fim. Separados por nada — chegam juntos, como numa tecla de
   * verdade — e apagados em menos de cinco centésimos.
   */
  letra: [
    { fonte: 'ruido', hz: 2800, q: 1.1, duracao: 0.02, volume: 0.1 },
    { fonte: 'tom', hz: 170, duracao: 0.045, volume: 0.055 },
  ],
  /*
   * Mesma mecânica, banda mais baixa e corpo mais grave: apagar é o contrário
   * de escrever, e o ouvido lê essa diferença sem que ninguém a explique.
   */
  apagar: [
    { fonte: 'ruido', hz: 1500, q: 1, duracao: 0.028, volume: 0.085 },
    { fonte: 'tom', hz: 110, duracao: 0.06, volume: 0.06 },
  ],
  /*
   * O clique da interface é mais curto e mais agudo que o da tecla: são coisas
   * diferentes, e apertar um botão não pode soar como escrever uma letra.
   */
  clique: [
    { fonte: 'ruido', hz: 3400, q: 2.2, duracao: 0.013, volume: 0.07 },
    { fonte: 'tom', hz: 240, duracao: 0.028, volume: 0.03 },
  ],
  /*
   * O sino de fim de linha da máquina de escrever — a única altura definida do
   * conjunto, e a única cauda longa. Duas parciais em razão inarmônica, que é
   * o que separa um sino de um apito: uma senoide sozinha, por mais bonita que
   * seja, volta a soar a videogame.
   *
   * O estalo que vem junto é o carro batendo na margem, e é ele que amarra o
   * sino ao resto — sem o ruído, o fim da palavra viria de outro jogo.
   */
  jogada: [
    { fonte: 'ruido', hz: 2600, q: 1.4, duracao: 0.02, volume: 0.07 },
    { fonte: 'tom', hz: 1568, duracao: 0.85, volume: 0.045, atraso: 0.012 },
    { fonte: 'tom', hz: 2349, duracao: 0.45, volume: 0.016, atraso: 0.012 },
  ],
};

/**
 * Duas teclas seguidas nunca soam idênticas.
 *
 * Numa máquina de verdade cada tecla bate um pouco diferente, e repetir a
 * mesma amostra doze vezes é justamente o que denuncia o sintético. O ruído
 * varia sozinho — cada estalo é lido de um ponto diferente do buffer —, e o
 * corpo grave segue esta sequência. Fixa e cíclica de propósito: aleatório de
 * verdade dá saltos grandes de vez em quando, e aí uma tecla pula fora do
 * conjunto.
 */
const DESVIOS = [1, 1.04, 0.98, 1.02, 0.96, 1.01];
let passo = 0;

/**
 * Um milissegundo de subida, e não zero.
 *
 * Ligar o ganho no seco produz um clique de amplitude — um defeito, não o
 * estalo que se quer — e ele soa por cima de tudo o mais.
 */
const ATAQUE = 0.001;
/** Ruído branco pronto para recortar; 200 ms dão folga para variar o ponto. */
const RUIDO_S = 0.2;

let contexto: AudioContext | undefined;
let ruido: AudioBuffer | undefined;
let ligado = true;

type ComWebkit = typeof globalThis & { webkitAudioContext?: typeof AudioContext };

/**
 * O contexto nasce no primeiro som, e não na carga da página: navegador
 * nenhum deixa tocar antes de um gesto, e um contexto criado cedo demais fica
 * suspenso para sempre. Todo caminho até aqui parte de uma tecla ou de um
 * toque, então o gesto sempre existe — o `resume` cobre o caso da aba que
 * voltou do fundo com o contexto adormecido.
 */
function pegarContexto(): AudioContext | undefined {
  const Construtor = globalThis.AudioContext ?? (globalThis as ComWebkit).webkitAudioContext;
  if (Construtor === undefined) return undefined;

  contexto ??= new Construtor();
  if (contexto.state === 'suspended') void contexto.resume();
  return contexto;
}

/** Um só buffer de ruído para a sessão inteira: cada estalo é um recorte dele. */
function bufferDeRuido(ctx: AudioContext): AudioBuffer {
  if (ruido !== undefined && ruido.sampleRate === ctx.sampleRate) return ruido;

  const quadros = Math.ceil(ctx.sampleRate * RUIDO_S);
  const buffer = ctx.createBuffer(1, quadros, ctx.sampleRate);
  const canal = buffer.getChannelData(0);
  for (let i = 0; i < quadros; i += 1) canal[i] = Math.random() * 2 - 1;

  ruido = buffer;
  return buffer;
}

/** Envelope percussivo: sobe num milissegundo e cai até o silêncio. */
function envelope(ctx: AudioContext, inicio: number, fim: number, volume: number): GainNode {
  const ganho = ctx.createGain();
  ganho.gain.setValueAtTime(0, inicio);
  ganho.gain.linearRampToValueAtTime(volume, inicio + ATAQUE);
  // `exponentialRamp` não aceita zero; 0,0001 é silêncio para qualquer ouvido.
  ganho.gain.exponentialRampToValueAtTime(0.0001, fim);
  return ganho;
}

function soar(ctx: AudioContext, camada: Camada, desvio: number): void {
  const inicio = ctx.currentTime + (camada.atraso ?? 0);
  const fim = inicio + camada.duracao;
  const ganho = envelope(ctx, inicio, fim, camada.volume);
  ganho.connect(ctx.destination);

  if (camada.fonte === 'ruido') {
    const fonte = ctx.createBufferSource();
    fonte.buffer = bufferDeRuido(ctx);

    const filtro = ctx.createBiquadFilter();
    filtro.type = 'bandpass';
    filtro.frequency.value = camada.hz * desvio;
    filtro.Q.value = camada.q;

    fonte.connect(filtro);
    filtro.connect(ganho);
    // Ponto de leitura sorteado: é o que faz dois estalos seguidos diferirem.
    fonte.start(inicio, Math.random() * (RUIDO_S - camada.duracao), camada.duracao + 0.01);
    return;
  }

  const oscilador = ctx.createOscillator();
  oscilador.type = 'sine';
  oscilador.frequency.value = camada.hz * desvio;
  oscilador.connect(ganho);
  oscilador.start(inicio);
  oscilador.stop(fim + 0.02);
}

export function tocar(som: Som): void {
  if (!ligado) return;
  const ctx = pegarContexto();
  if (ctx === undefined) return;

  // O sino é afinado: desafiná-lo a cada jogada soaria a instrumento quebrado.
  const desvio = som === 'jogada' ? 1 : (DESVIOS[passo++ % DESVIOS.length] ?? 1);
  for (const camada of CAMADAS[som]) soar(ctx, camada, desvio);
}

export function definirSom(valor: boolean): void {
  ligado = valor;
}

export function somAtivo(): boolean {
  return ligado;
}

/**
 * O som dos cliques, num ouvinte só.
 *
 * Espalhar `tocar('clique')` por cada `onClick` seria a mesma linha repetida
 * em vinte lugares, e o vigésimo primeiro botão nasceria mudo. Um ouvinte
 * delegado no documento acerta todos, inclusive os que ainda não existem.
 *
 * O teclado do jogo fica de fora: as teclas dele mexem no draft, e quem
 * anuncia isso é `somDaMudanca` — sem a exceção, cada letra soaria duas vezes.
 */
export function ouvirCliques(): () => void {
  const aoApontar = (e: PointerEvent) => {
    if (!(e.target instanceof Element)) return;
    if (e.target.closest('.teclado') !== null) return;
    if (e.target.closest('button, summary, [role="button"]') === null) return;
    tocar('clique');
  };

  document.addEventListener('pointerdown', aoApontar);
  return () => document.removeEventListener('pointerdown', aoApontar);
}

/**
 * Que som cabe entre dois instantes do jogo.
 *
 * Ler a mudança do estado, e não os manipuladores, é o que faz o teclado
 * físico, o teclado do jogo, o desfazer e a colagem soarem igual: todos passam
 * por aqui porque todos terminam no mesmo redutor. Amarrar o som aos eventos
 * deixaria de fora justamente os caminhos que ninguém lembra de testar.
 *
 * A cadeia tem precedência sobre o draft porque confirmar a palavra faz as
 * duas coisas ao mesmo tempo — o draft esvazia, e isso não é apagar.
 */
export function somDaMudanca(
  anterior: { readonly draft: string; readonly palavras: number },
  atual: { readonly draft: string; readonly palavras: number },
): Som | undefined {
  if (atual.palavras > anterior.palavras) return 'jogada';

  const antes = tamanhoVisivel(anterior.draft);
  const agora = tamanhoVisivel(atual.draft);
  if (agora > antes) return 'letra';
  if (agora < antes) return 'apagar';
  return undefined;
}
